'use client';

import { DestinationCGType, SearchResultCGType } from '@/types/graphql/root';
import { DayPinColor, filterNullUndefined, hexToRgba } from '@/utils';
import MapboxLanguage from '@mapbox/mapbox-gl-language';
import { Map, Marker } from 'mapbox-gl';
import { useParams, usePathname, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useMemo, useState } from 'react';
import ReactDOMServer from 'react-dom/server';
import DestinationMarkerSvg from '../ui/trip/destination-marker-svg';
import Image from 'next/image';
import savedPoiCircleImage from '@/images/saved_poi_circle@2x.png';
import SearchResultMarkerSvg from '../ui/trip/search-result-marker-svg';
import AccommWhite1212 from '@svg/trip/accomm_white_12_12.svg';
import ActivityWhite1212 from '@svg/trip/activity_white_12_12.svg';
import AttractionWhite1212 from '@svg/trip/attraction_white_12_12.svg';
import CafeWhite1212 from '@svg/trip/cafe_white_12_12.svg';
import RestaurantWhite1212 from '@svg/trip/restaurant_white_12_12.svg';
import ShoppingWhite1212 from '@svg/trip/shopping_white_12_12.svg';
import SearchAgainWhite1616 from '@svg/trip/search_again_white_16_16.svg';
import { searchPoisUsingCoords } from '@/utils/actions';
import { history } from '@/utils/history';
import useGetAllSavedPoisQuery from './useGetAllSavedPoisQuery';
import { calculateCardinalPoints } from '@/utils/map-calc';
import {
  ITINERARY_HEIGHT_MODE,
  ITINERARY_HEIGHT_MODE_CONDITIONS,
  ItineraryHeightModeType,
  TRIP_CONDITIONS,
  TRIP_MODE,
  TripModeType,
} from '@/app/ui/trip/client-trip-page';

const MAPBOX_TOKEN =
  'pk.eyJ1IjoiYnJpYW4tc3RvcmljaXR5IiwiYSI6ImNrbWtiMGk1OTAxMWsydm44Y3N3OGk2MTIifQ.eF3l3os6bCq_hopZn87LcA';

const getDistance = (
  pointA: {
    lat: number;
    lng: number;
  },
  pointB: {
    lat: number;
    lng: number;
  },
) => {
  const R = 6371e3; // metres
  const φ1 = (pointA.lat * Math.PI) / 180; // φ, λ in radians
  const φ2 = (pointB.lat * Math.PI) / 180;
  const Δφ = ((pointB.lat - pointA.lat) * Math.PI) / 180;
  const Δλ = ((pointB.lng - pointA.lng) * Math.PI) / 180;

  const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
};

const getDistanceFromCenterAndWestAndNorth = (
  center: { latitude?: number; longitude?: number },
  west: number,
  north: number,
) => {
  if (!center.latitude || !center.longitude) return null;

  const horizontalRaidus = center.longitude - west;
  const verticalRadius = north - center.latitude;
  const radius = Math.max(horizontalRaidus, verticalRadius);
  const distance = radius * 111.32;

  return distance * 1000;
};

type UseMapProps = {
  destinations: DestinationCGType[];
  mode: TripModeType;
  itineraryHeightMode: ItineraryHeightModeType;
  setItineraryHeightMode: React.Dispatch<React.SetStateAction<ItineraryHeightModeType>>;
  initCenter: number[] | null;
  scrollListToDestOnTop: (destId: string, day: number) => void;
  initTopSlot: DestinationCGType | null;
  searchValue: string;
  setSearchValue: React.Dispatch<React.SetStateAction<string>>;
  selectedSearchResult: SearchResultCGType | null;
  setSelectedSearchResult: React.Dispatch<React.SetStateAction<SearchResultCGType | null>>;
  searchResults: SearchResultCGType[];
  setSearchResults: React.Dispatch<React.SetStateAction<SearchResultCGType[]>>;
  setLoadingSearchPois: React.Dispatch<React.SetStateAction<boolean>>;
  lastSearchCenterAndZoom: {
    lat: number;
    lng: number;
    zoom: number;
  } | null;
  setLastSearchCenterAndZoom: React.Dispatch<
    React.SetStateAction<{
      lat: number;
      lng: number;
      zoom: number;
    } | null>
  >;
  selectedPoiId: string | null;
  onClickSearch: (value: string) => void;
};

export default function useMap({
  destinations,
  mode,
  itineraryHeightMode,
  setItineraryHeightMode,
  initCenter,
  scrollListToDestOnTop,
  initTopSlot,
  searchValue,
  setSearchValue,
  selectedSearchResult,
  setSelectedSearchResult,
  searchResults,
  setSearchResults,
  lastSearchCenterAndZoom,
  setLastSearchCenterAndZoom,
  onClickSearch,
  setLoadingSearchPois,
  selectedPoiId,
}: UseMapProps) {
  const pathname = usePathname();
  const params = useParams();
  const searchParams = useSearchParams();

  const savedPois = useGetAllSavedPoisQuery();

  const [map, setMap] = useState<Map | null>(null);
  const [topDest, setTopDest] = useState<DestinationCGType | null>(initTopSlot);
  const [centerAndZoom, setCenterAndZoom] = useState<{
    lat: number;
    lng: number;
    zoom: number;
  } | null>(null);

  const setTopDestInMap = useCallback((_topDest: DestinationCGType | null) => {
    setTopDest(_topDest);
  }, []);

  // 지도 생성
  useEffect(() => {
    const showErrorMessage = ($mapEl: HTMLElement) => {
      const errorMessage = document.createElement('div');
      errorMessage.className = 'map-error-message';
      errorMessage.style.paddingTop = '10px';
      errorMessage.style.textAlign = 'center';
      errorMessage.innerHTML = `
        <p>지도를 로드하는 중 문제가 발생했습니다.</p>
        <p>브라우저의 하드웨어 가속이 활성화되어 있는지 확인해주세요.</p>
      `;
      $mapEl.appendChild(errorMessage);
    };

    try {
      const $mapEl = document.getElementById('planner-map');
      if (!$mapEl) return;

      const center: [number, number] = [128.070053, 36.679994];
      const zoom = 7;
      const _map = new Map({
        accessToken: MAPBOX_TOKEN,
        container: $mapEl,
        style: 'mapbox://styles/brian-storicity/claqci8ds003o14pksmda6r75',
        center,
        zoom,
        attributionControl: false,
        failIfMajorPerformanceCaveat: true,
      });

      _map.addControl(new MapboxLanguage());
      _map.touchPitch.disable();
      _map.setMaxZoom(18);
      _map.setMinZoom(5);

      const $mapboxLogo = document.querySelectorAll('.mapboxgl-ctrl-logo');
      $mapboxLogo.forEach(($el) => $el.remove());

      setMap(_map);
    } catch (error) {
      console.error('Error while creating map:', error);
      const $mapEl = document.getElementById('planner-map');
      if ($mapEl) showErrorMessage($mapEl);
    }
  }, []);

  useEffect(() => {
    if (!map) return;

    const onMoveEnd = () => {
      const $plannerMap = document.getElementById('planner-map');
      if (ITINERARY_HEIGHT_MODE_CONDITIONS.IS_MINIMALIZE(itineraryHeightMode)) {
        setCenterAndZoom({
          lat: map.getCenter().lat,
          lng: map.getCenter().lng,
          zoom: map.getZoom(),
        });
      } else {
        if (!$plannerMap) return;
        const plannerMapHeight = $plannerMap.clientHeight;
        const mapNorth = map.getBounds()?.getNorth() ?? 0;
        const mapSouth = map.getBounds()?.getSouth() ?? 0;
        const northAndSouthDiff = mapNorth - mapSouth;
        const fixedMapSouth = mapNorth - (northAndSouthDiff / plannerMapHeight) * 236;
        setCenterAndZoom({
          lat: (mapNorth + fixedMapSouth) / 2,
          lng: map.getCenter().lng,
          zoom: map.getZoom(),
        });
      }
    };

    map.on('moveend', onMoveEnd);

    return () => {
      map.off('moveend', onMoveEnd);
    };
  }, [map, itineraryHeightMode]);

  const currentDay = useMemo(() => topDest?.day ?? 1, [topDest]);

  const sortedDestinations = useMemo(() => {
    let indexOfDay = 1;
    let currentDay = 1;
    return destinations
      .map((dest) => {
        if (dest.day && dest.day > currentDay) {
          indexOfDay = 1;
          currentDay = dest.day;
        }
        const destWithIndexOfDay = { ...dest, indexOfDay };
        indexOfDay++;
        return destWithIndexOfDay;
      })
      .sort((a, b) => {
        if (!a.poi?.latitude) return 0;
        else if (!b.poi?.latitude) return 0;

        if (a.poi.latitude > b.poi?.latitude) return -1;
        else if (a.poi.latitude < b.poi.latitude) return 1;
        return 0;
      });
  }, [destinations]);

  const sortedSearchResults = useMemo(() => {
    if (mode !== TRIP_MODE.SEARCH) return [];
    return searchResults.sort((a, b) => {
      if (!a.latitude) return 0;
      else if (!b.latitude) return 0;

      if (a.latitude > b.latitude) return -1;
      else if (a.latitude < b.latitude) return 1;
      return 0;
    });
  }, [searchResults, searchValue, mode]);

  useEffect(() => {
    if (!map || sortedSearchResults.length === 0) return;

    const newMarkers: Marker[] = [];
    sortedSearchResults.forEach((searchResult) => {
      if (!searchResult.latitude || !searchResult.longitude) return;

      const isSelected = selectedSearchResult?.placeId === searchResult.placeId;

      const $markerContainer = document.createElement('div');
      $markerContainer.style.fontFamily = 'Noto Sans KR';
      $markerContainer.className = 'flex flex-col items-center max-w-90pxr gap-4pxr';
      $markerContainer.style.zIndex = isSelected ? '5' : '4';
      const $markerBox = document.createElement('div');
      $markerBox.className = 'relative';

      $markerBox.innerHTML = ReactDOMServer.renderToString(
        <>
          <SearchResultMarkerSvg
            key={`search-result-svg_${searchResult.placeId}`}
            width={isSelected ? 27 : 18}
            height={isSelected ? 36 : 24}
          />
        </>,
      );
      $markerContainer.appendChild($markerBox);
      $markerBox.onclick = () => {
        // onClickSearchPoi(searchResult);
      };

      const $iconSpan = document.createElement('span');
      $iconSpan.className = 'absolute flex justify-center w-full top-4pxr';
      if (isSelected) {
        $iconSpan.style.transform = 'scale(1.5)';
        $iconSpan.style.top = '8px';
      }
      if (searchResult.poiType === '식당') {
        $iconSpan.innerHTML = ReactDOMServer.renderToString(<RestaurantWhite1212 />);
      } else if (searchResult.poiType === '숙소') {
        $iconSpan.innerHTML = ReactDOMServer.renderToString(<AccommWhite1212 />);
      } else if (searchResult.poiType === '카페') {
        $iconSpan.innerHTML = ReactDOMServer.renderToString(<CafeWhite1212 />);
      } else if (searchResult.poiType === '쇼핑') {
        $iconSpan.innerHTML = ReactDOMServer.renderToString(<ShoppingWhite1212 />);
      } else if (searchResult.poiType === '관광스팟') {
        $iconSpan.innerHTML = ReactDOMServer.renderToString(<AttractionWhite1212 />);
      } else {
        $iconSpan.innerHTML = ReactDOMServer.renderToString(<ActivityWhite1212 />);
      }
      $markerBox.appendChild($iconSpan);

      const $nameBox = document.createElement('div');
      $nameBox.innerHTML = searchResult.name ?? '';
      $nameBox.className =
        'max-w-90pxr text-gray-800 bg-white bg-opacity-90 leading-12pxr text-12pxr h-13pxr font-medium -tracking-[0.5px] text-center px-2pxr rounded-3pxr truncate';
      if (isSelected) {
        $nameBox.className =
          'max-w-100pxr text-white bg-custom-black bg-opacity-70 leading-normal text-14pxr font-medium -tracking-[0.5px] text-center px-2pxr rounded-3pxr truncate';
      }
      $markerContainer.appendChild($nameBox);

      const marker = new Marker({
        element: $markerContainer,
        anchor: 'top',
        offset: [0, -24],
      }).setLngLat([searchResult.longitude, searchResult.latitude]);
      newMarkers.push(marker);
    });

    // setSearchResultMarkers(newMarkers);

    newMarkers.forEach((marker) => {
      marker.addTo(map);
    });

    return () => {
      newMarkers.forEach((marker) => {
        marker.remove();
      });
    };
  }, [map, sortedSearchResults, selectedSearchResult]);

  const poiDetailId = useMemo(() => {
    if (pathname.includes('poi')) {
      const spilitedPathname = pathname.split('/');
      const poiId = spilitedPathname[spilitedPathname.length - 1];
      return poiId;
    }
    return null;
  }, [pathname]);

  const tripId = useMemo(() => {
    return params.id;
  }, [params]);

  useEffect(() => {
    if (!map || sortedDestinations.length === 0) return;

    const newMarkers: Marker[] = [];
    sortedDestinations.forEach((dest) => {
      if (!dest.poi?.latitude || !dest.poi?.longitude) return;

      const $markerContainer = document.createElement('div');
      $markerContainer.style.fontFamily = 'Noto Sans KR';
      $markerContainer.className = 'flex flex-col items-center max-w-90pxr gap-4pxr';
      $markerContainer.style.zIndex = (currentDay === dest.day ? 3 : 2).toString();

      const $markerBox = document.createElement('div');
      $markerBox.className = 'relative';
      let bgColor = '';
      if (dest.day === 1) bgColor = DayPinColor.day1;
      else if (dest.day === 2) bgColor = DayPinColor.day2;
      else if (dest.day === 3) bgColor = DayPinColor.day3;
      else if (dest.day === 4) bgColor = DayPinColor.day4;
      else if (dest.day === 5) bgColor = DayPinColor.day5;
      else if (dest.day === 6) bgColor = DayPinColor.day6;

      if (
        topDest?.id === dest.id &&
        sortedSearchResults.length === 0 &&
        (poiDetailId === null || poiDetailId === dest.poi?.id)
      ) {
        $markerContainer.style.zIndex = '4';
        $markerBox.style.transformOrigin = 'bottom center';
        $markerBox.style.transform = 'scale(1.5)';
      }

      $markerBox.innerHTML = ReactDOMServer.renderToString(
        <>
          <DestinationMarkerSvg
            key={`marker-svg_${dest.day}_${dest.indexOfDay}`}
            bgColor={bgColor}
            width={18}
            height={24}
          />
        </>,
      );

      const onClickMarker = (destId: string, poiId: string | null) => {
        const $destCard = document.getElementById(`destCard_${destId}`);
        if (!$destCard || mode === TRIP_MODE.SEARCH) return;

        scrollListToDestOnTop(destId, dest?.day ?? 0);
        if (TRIP_CONDITIONS.IS_VIEW_MODE(mode)) {
          const alreadyPoiId = searchParams.get('poiId');
          if (alreadyPoiId) {
            history?.replace(`/trip/${tripId}?poiId=${poiId}`);
          } else {
            history?.push(`/trip/${tripId}?poiId=${poiId}`);
          }
        }
        setItineraryHeightMode((prev) => {
          if (prev === ITINERARY_HEIGHT_MODE.MINIMALIZE) {
            return ITINERARY_HEIGHT_MODE.DEFAULT;
          }
          return prev;
        });
      };

      $markerBox.onclick = () => {
        onClickMarker(dest.id ?? '', dest.poi?.id ?? null);
      };

      $markerContainer.appendChild($markerBox);
      if (currentDay === dest.day) {
        const $indexSpan = document.createElement('span');
        $indexSpan.className = 'absolute text-center text-white top-1pxr w-18pxr';
        $indexSpan.style.fontFamily = 'Roboto';
        $indexSpan.innerHTML = dest.indexOfDay.toString();
        $markerBox.appendChild($indexSpan);

        if (sortedSearchResults.length === 0) {
          const $nameBox = document.createElement('div');
          $nameBox.innerHTML = dest.poi.name;
          $nameBox.className =
            'max-w-90pxr text-gray-800 bg-white bg-opacity-90 leading-16pxr text-12pxr h-13pxr font-medium -tracking-[0.5px] text-center px-2pxr rounded-3pxr truncate';
          if (topDest?.id === dest.id && (poiDetailId === null || poiDetailId === dest.poi?.id)) {
            $nameBox.className =
              'max-w-100pxr text-white bg-custom-black bg-opacity-70 leading-normal text-14pxr font-medium -tracking-[0.5px] text-center px-2pxr rounded-3pxr truncate';
          }
          $markerContainer.appendChild($nameBox);
        }
      }

      const marker = new Marker({
        element: $markerContainer,
        anchor: 'top',
        offset: [0, -24],
      }).setLngLat([dest.poi.longitude, dest.poi.latitude]);
      newMarkers.push(marker);
    });

    // setDestinationMarkers(newMarkers);

    newMarkers.forEach((marker) => {
      marker.addTo(map);
    });

    return () => {
      newMarkers.forEach((marker) => {
        marker.remove();
      });
    };
  }, [map, sortedDestinations, currentDay, topDest, sortedSearchResults, poiDetailId, mode, pathname, searchParams]);

  useEffect(() => {
    /* view mode로 변경시 현재 포커스된 일자마커들 기준으로 지도 위치 재조정 */
    if (!map) return;

    if (TRIP_CONDITIONS.IS_VIEW_MODE(mode)) {
      const currentDayDestinations = sortedDestinations.filter((dest) => dest.day === currentDay);
      const currentDayPoiCoordinates = filterNullUndefined(currentDayDestinations.map((dest) => dest.poi)).map(
        (poi) => ({
          latitude: poi.latitude ?? null,
          longitude: poi.longitude ?? null,
        }),
      );
      const cardinalPoints = calculateCardinalPoints(currentDayPoiCoordinates, {
        latitude: initCenter?.[1] ?? null,
        longitude: initCenter?.[0] ?? null,
      });

      setTimeout(() => {
        const itineraryHeight = window.innerHeight - 56 - 236;
        map.setMaxZoom(16);
        if (ITINERARY_HEIGHT_MODE_CONDITIONS.IS_MINIMALIZE(itineraryHeightMode)) {
          map.fitBounds(
            [
              [cardinalPoints.west ?? -180, cardinalPoints.south ?? -90],
              [cardinalPoints.east ?? 180, cardinalPoints.north ?? 90],
            ],
            {
              padding: { left: 20, right: 20, top: 190, bottom: 130 },
              duration: 100,
            },
          );
        } else {
          map.fitBounds(
            [
              [cardinalPoints.west ?? -180, cardinalPoints.south ?? -90],
              [cardinalPoints.east ?? 180, cardinalPoints.north ?? 90],
            ],
            {
              padding: {
                left: 20,
                right: 20,
                top: 90,
                bottom: itineraryHeight + 30,
              },
              duration: 100,
            },
          );
        }
        map.setMaxZoom(18);
      }, 150);
    }
  }, [map, mode]);

  useEffect(() => {
    /* search mode에서 검색결과가 있을때, 검색결과 기준으로 지도 위치 재조정 */
    if (!map || sortedSearchResults.length === 0) return;

    const sortedSearchResultsCoordinates = filterNullUndefined(
      sortedSearchResults.map((result) => ({
        latitude: result.latitude ?? null,
        longitude: result.longitude ?? null,
      })),
    );
    const cardinalPoints = calculateCardinalPoints(sortedSearchResultsCoordinates);

    setTimeout(() => {
      const itineraryHeight = (window?.innerHeight ?? 0) - 56 - 236;
      map.setMaxZoom(16);
      if (ITINERARY_HEIGHT_MODE_CONDITIONS.IS_MINIMALIZE(itineraryHeightMode)) {
        map.fitBounds(
          [
            [cardinalPoints.west ?? -180, cardinalPoints.south ?? -90],
            [cardinalPoints.east ?? 180, cardinalPoints.north ?? 90],
          ],
          {
            padding: { left: 20, right: 20, top: 190, bottom: 130 },
            duration: 100,
          },
        );
      } else {
        map.fitBounds(
          [
            [cardinalPoints.west ?? -180, cardinalPoints.south ?? -90],
            [cardinalPoints.east ?? 180, cardinalPoints.north ?? 90],
          ],
          {
            padding: {
              left: 20,
              right: 20,
              top: 90,
              bottom: itineraryHeight + 30,
            },
            duration: 100,
          },
        );
      }
      map.setMaxZoom(18);
    }, 150);
  }, [map, sortedSearchResults, itineraryHeightMode]);

  useEffect(() => {
    /* currentDay가 바뀔때(스크롤링으로 인한 일정표 탐색), 현재 일자의 마커들 기준으로 지도 위치 재조정 */
    if (!map) return;

    const currentDayDestinations = sortedDestinations.filter((dest) => dest.day === currentDay);
    const currentDayPoiCoordinates = filterNullUndefined(currentDayDestinations.map((dest) => dest.poi)).map((poi) => ({
      latitude: poi.latitude ?? null,
      longitude: poi.longitude ?? null,
    }));
    const cardinalPoints = calculateCardinalPoints(currentDayPoiCoordinates, {
      latitude: initCenter?.[1] ?? null,
      longitude: initCenter?.[0] ?? null,
    });

    setTimeout(() => {
      const itineraryHeight = window.innerHeight - 56 - 236;
      map.setMaxZoom(16);
      if (ITINERARY_HEIGHT_MODE_CONDITIONS.IS_MINIMALIZE(itineraryHeightMode)) {
        map.fitBounds(
          [
            [cardinalPoints.west ?? -180, cardinalPoints.south ?? -90],
            [cardinalPoints.east ?? 180, cardinalPoints.north ?? 90],
          ],
          {
            padding: { left: 20, right: 20, top: 190, bottom: 130 },
            duration: 100,
          },
        );
      } else {
        map.fitBounds(
          [
            [cardinalPoints.west ?? -180, cardinalPoints.south ?? -90],
            [cardinalPoints.east ?? 180, cardinalPoints.north ?? 90],
          ],
          {
            padding: {
              left: 20,
              right: 20,
              top: 90,
              bottom: itineraryHeight + 30,
            },
            duration: 100,
          },
        );
      }
      map.setMaxZoom(18);
    }, 150);
  }, [map, sortedDestinations, currentDay, itineraryHeightMode]);

  useEffect(() => {
    if (!map) return;
    const [west, south, east, north] = map.getBounds()?.toArray().flat() ?? [];

    const destIdList = destinations.map((dest) => dest.poi?.id);
    const center = {
      lat: (north + south) / 2,
      lng: (east + west) / 2,
    };
    const _filteredSavedPois = (savedPois ?? [])
      .filter((savedPoi) => !destIdList.includes(savedPoi.id))
      .filter((savedPoi) => {
        if (!savedPoi.latitude || !savedPoi.longitude) {
          return false;
        }
        return (
          savedPoi.latitude > south - 1 &&
          savedPoi.latitude < north + 1 &&
          savedPoi.longitude > west - 1 &&
          savedPoi.longitude < east + 1
        );
      })
      .sort(
        (a, b) =>
          getDistance(center, { lat: a.latitude ?? 0, lng: a.longitude ?? 0 }) -
          getDistance(center, { lat: b.latitude ?? 0, lng: b.longitude ?? 0 }),
      );

    const sortedSavedPois = _filteredSavedPois.slice(0, 100).sort((a, b) => {
      if (!a.latitude) return 0;
      else if (!b.latitude) return 0;

      if (a.latitude > b.latitude) return -1;
      else if (a.latitude < b.latitude) return 1;
      return 0;
    });

    const newMarkers: Marker[] = [];
    sortedSavedPois.forEach((savedPoi) => {
      if (!savedPoi.latitude || !savedPoi.longitude) return;

      const isSelected = poiDetailId === savedPoi.id;
      const $markerContainer = document.createElement('div');
      $markerContainer.style.fontFamily = 'Noto Sans KR';
      $markerContainer.className = 'flex flex-col items-center max-w-90pxr gap-4pxr';
      $markerContainer.style.zIndex = '1';
      const $markerBox = document.createElement('div');
      $markerBox.className = 'relative';

      $markerBox.innerHTML = ReactDOMServer.renderToString(
        <Image src={savedPoiCircleImage} className='w-18pxr h-18pxr' alt='saved-poi-marker' width={18} height={18} />,
      );
      $markerBox.onclick = () => {
        const tripIdPattern = /^\/trip\/[^/]+\/poi\/.*$/;
        if (tripIdPattern.test(pathname)) {
          history?.replace(`/trip/${tripId}?poiId=${savedPoi.id}`);
        } else {
          history?.push(`/trip/${tripId}?poiId=${savedPoi.id}`);
        }
      };
      $markerContainer.appendChild($markerBox);

      if (sortedSavedPois.length < 50 || isSelected) {
        const $nameBox = document.createElement('div');
        $nameBox.innerHTML = savedPoi.originName ?? '';
        $nameBox.className =
          'max-w-90pxr text-gray-800 bg-white bg-opacity-90 leading-16pxr text-12pxr h-13pxr font-medium -tracking-[0.5px] text-center px-2pxr rounded-3pxr truncate';
        $markerContainer.appendChild($nameBox);

        if (isSelected) {
          $nameBox.className =
            'max-w-100pxr text-white bg-custom-black bg-opacity-70 leading-normal text-14pxr font-medium -tracking-[0.5px] text-center px-2pxr rounded-3pxr truncate';
        }
      }

      if (isSelected) {
        $markerContainer.style.zIndex = '5';
        $markerBox.style.transform = 'scale(1.5)';
      }

      const marker = new Marker({
        element: $markerContainer,
        anchor: 'top',
        offset: [0, -9],
      }).setLngLat([savedPoi.longitude, savedPoi.latitude]);
      newMarkers.push(marker);
    });

    newMarkers.forEach((marker) => marker.addTo(map));

    return () => {
      newMarkers.forEach((marker) => marker.remove());
    };
  }, [map, centerAndZoom, savedPois, destinations, poiDetailId]);

  const SearchHelperChipsComponent = useMemo(() => {
    if (
      searchValue !== '' ||
      !TRIP_CONDITIONS.IS_SEARCH_MODE(mode) ||
      ITINERARY_HEIGHT_MODE_CONDITIONS.IS_FULL(itineraryHeightMode)
    )
      return null;

    return (
      <div
        className={`absolute left-16pxr flex flex-row gap-12pxr z-10 ${
          ITINERARY_HEIGHT_MODE_CONDITIONS.IS_DEFAULT(itineraryHeightMode) ? 'top-172pxr' : 'bottom-76pxr'
        }`}
      >
        <button
          className='flex-none text-gray-700 bg-white border border-gray-200 rounded-full px-18pxr py-10pxr button-14-m search-chip-btn'
          style={{ boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.30)' }}
          onClick={() => {
            setSearchValue('명소');
            onClickSearch('명소');
          }}
        >
          명소
        </button>
        <button
          className='flex-none text-gray-700 bg-white border border-gray-200 rounded-full px-18pxr py-10pxr button-14-m search-chip-btn'
          style={{ boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.30)' }}
          onClick={() => {
            setSearchValue('식당');
            onClickSearch('식당');
          }}
        >
          식당
        </button>
        <button
          className='flex-none text-gray-700 bg-white border border-gray-200 rounded-full px-18pxr py-10pxr button-14-m search-chip-btn'
          style={{ boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.30)' }}
          onClick={() => {
            setSearchValue('카페');
            onClickSearch('카페');
          }}
        >
          카페
        </button>
        <button
          className='flex-none text-gray-700 bg-white border border-gray-200 rounded-full px-18pxr py-10pxr button-14-m search-chip-btn'
          style={{ boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.30)' }}
          onClick={() => {
            setSearchValue('숙소');
            onClickSearch('숙소');
          }}
        >
          숙소
        </button>
      </div>
    );
  }, [itineraryHeightMode, searchValue, mode, pathname]);

  const isValideSearchRetry = useMemo(() => {
    if (!map) return false;
    if (!centerAndZoom || !lastSearchCenterAndZoom || searchValue === '') return false;

    const latGap = Math.abs(centerAndZoom.lat - lastSearchCenterAndZoom.lat);
    const lngGap = Math.abs(centerAndZoom.lng - lastSearchCenterAndZoom.lng);
    const zoomGap = Math.abs(centerAndZoom.zoom - lastSearchCenterAndZoom.zoom);

    return latGap > 0.0001 || lngGap > 0.0001 || zoomGap > 0.01;
  }, [map, centerAndZoom, lastSearchCenterAndZoom, searchValue]);

  const onClickRetrySearch = useCallback(async () => {
    if (!map || !centerAndZoom?.lat || !centerAndZoom.lng) return;

    const distance = getDistanceFromCenterAndWestAndNorth(
      { latitude: centerAndZoom?.lat, longitude: centerAndZoom?.lng },
      map?.getBounds()?.getWest() ?? 0,
      map?.getBounds()?.getNorth() ?? 0,
    );
    setLoadingSearchPois(true);
    setSearchResults([]);
    setLastSearchCenterAndZoom({
      lat: centerAndZoom.lat,
      lng: centerAndZoom.lng,
      zoom: map.getZoom(),
    });
    const searchResponse = await searchPoisUsingCoords(searchValue, null, {
      latitude: centerAndZoom?.lat ?? 0,
      longitude: centerAndZoom?.lng ?? 0,
    });
    setLastSearchCenterAndZoom({
      lat: centerAndZoom?.lat ?? 0,
      lng: centerAndZoom?.lng ?? 0,
      zoom: map?.getZoom() ?? 0,
    });

    if (searchResponse) {
      setSearchResults(filterNullUndefined(searchResponse));
      setSelectedSearchResult(null);
    }
    setLoadingSearchPois(false);
  }, [searchValue, map, centerAndZoom]);

  const SearchRetryButtonComponent = useMemo(() => {
    if (ITINERARY_HEIGHT_MODE_CONDITIONS.IS_FULL(itineraryHeightMode) || !isValideSearchRetry || selectedPoiId)
      return null;

    return (
      <div
        className={`absolute  flex items-center justify-center w-full pb-16pxr ${
          ITINERARY_HEIGHT_MODE_CONDITIONS.IS_DEFAULT(itineraryHeightMode) ? 'top-184pxr' : 'bottom-64pxr'
        }`}
      >
        <button
          className='z-10 bg-[#669AFF] rounded-full px-10pxr py-8pxr flex flex-row gap-4pxr items-center'
          onClick={() => onClickRetrySearch()}
        >
          <SearchAgainWhite1616 className='flex-none' />
          <span className='text-white button-14-m'>현 지도에서 재검색</span>
        </button>
      </div>
    );
  }, [itineraryHeightMode, isValideSearchRetry, onClickRetrySearch, selectedPoiId]);

  const [isMapStyleLoaded, setIsMapStyleLoaded] = useState(false);

  useEffect(() => {
    const handleMapStyleLoad = () => {
      setIsMapStyleLoaded(true);
    };
    map?.on('style.load', handleMapStyleLoad);
    return () => {
      map?.off('style.load', handleMapStyleLoad);
    };
  }, [map]);

  useEffect(() => {
    if (!isMapStyleLoaded) return;
    if (!map) return;
    if (!destinations.length) return;

    try {
      const destsInDay = destinations.filter((dest) => dest.day === currentDay);
      const coords: [number, number][] = [];

      destsInDay.forEach((dest) => {
        if (!dest.poi?.longitude || !dest.poi?.latitude) return;
        coords.push([dest.poi.longitude, dest.poi.latitude]);
      });

      // 유효한 좌표가 2개 이상일 때만 라인을 그림
      if (coords.length >= 2) {
        map.addSource('route', {
          type: 'geojson',
          data: {
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'LineString',
              coordinates: coords,
            },
          },
        });

        const lineColor = DayPinColor[`day${currentDay}` as keyof typeof DayPinColor];
        const rgbaLineColor = hexToRgba(lineColor, 70);

        map.addLayer({
          id: 'route',
          type: 'line',
          source: 'route',
          layout: {
            'line-join': 'round',
            'line-cap': 'round',
          },
          paint: {
            'line-color': rgbaLineColor,
            'line-width': 3,
            'line-dasharray': [1, 3],
          },
        });
      }
    } catch (error) {
      console.error('Error adding route:', error);
    }

    // 컴포넌트 언마운트 시 정리
    return () => {
      if (map.getLayer('route')) {
        map.removeLayer('route');
      }
      if (map.getSource('route')) {
        map.removeSource('route');
      }
    };
  }, [isMapStyleLoaded, map, destinations, currentDay]);

  return {
    topDest,
    setTopDestInMap,
    map,
    centerAndZoom,
    SearchHelperChipsComponent,
    SearchRetryButtonComponent,
  };
}
